import { ColumnDef } from "@tanstack/react-table";
import {
  SelfAttestedIncome,
  SelfReportedIncomeSource
} from "types/landlord/reports";

import { Section } from "views/applications-id-report/components/Section";
import { UniversalTable } from "views/applications-id-report/slices/IncomeReportSlice/components/UniversalTable";
import { selfAttestedIncomeSourcesColumns } from "../components/SelfReportedIncomeSourcesTableExpander/SelfAttestedIncomeSourcesColumns";
import moment from "moment";
import { getSelfAttestedIncomeSourceInfo } from "../components/VerifiedIncomeSourcesTableExpander/utils";
import { formatToCurrency } from "utils/currency";

interface SelfReportedIncomeSourcesProps {
  report?: SelfAttestedIncome | null;
  offerLetterScroll?: React.RefObject<HTMLDivElement>;
}

export const SelfReportedIncomeSources = ({
  report,
  offerLetterScroll
}: SelfReportedIncomeSourcesProps) => {
  const { income_sources } = report || {};

  const data = income_sources
    ?.filter(
      (source) =>
        source.income_source !==
        SelfReportedIncomeSource.HousingAssistanceProgram
    )
    .sort((a, b) => {
      const typeA = a.income_source.toLowerCase().replaceAll(/[ -]+/g, "_");
      const typeB = b.income_source.toLowerCase().replaceAll(/[ -]+/g, "_");

      const aIsOfferLetter = typeA === "offer_letter";
      const bIsOfferLetter = typeB === "offer_letter";

      // If one is an offer letter and the other isn't, prioritize the offer letter
      if (aIsOfferLetter && !bIsOfferLetter) return -1;
      if (!aIsOfferLetter && bIsOfferLetter) return 1;

      // If both are offer letters or both are not, sort by date in descending order
      const dateA = moment(a[typeA]?.start_date);
      const dateB = moment(b[typeB]?.start_date);

      return dateB.diff(dateA);
    });

  return (
    <Section sectionTitle="Self-reported income sources" maxWidth="none">
      <div ref={offerLetterScroll}>
        <UniversalTable
          tableColumns={
            selfAttestedIncomeSourcesColumns as ColumnDef<unknown, any>[]
          }
          tableData={data}
          sectionName="Self-reported income sources"
          rowHeight={76}
          mobileRow={(row) => {
            const data = getSelfAttestedIncomeSourceInfo(row.original);

            const employment = row.original;
            const currentTypeOfSelfAttested = row.original?.income_source
              .toLowerCase()
              .replaceAll(/[ -]+/g, "_");
            const endDate = employment?.[currentTypeOfSelfAttested]?.end_date
              ? moment(employment[currentTypeOfSelfAttested]?.end_date).format(
                  "MMM DD, YYYY"
                )
              : "Present";

            return (
              <div>
                <div className="flex gap-2">
                  <div className="">
                    <p className="text-content-1000 text-sm font-medium">
                      {data.title}
                    </p>
                    <p className="text-content-400 text-xs font-normal">
                      {data.subtitle}
                    </p>
                  </div>

                  <div className="ml-auto">
                    <p className="text-content-1000 text-sm font-medium">
                      {formatToCurrency(row.original.monthly_income)}
                    </p>
                  </div>
                </div>
                {currentTypeOfSelfAttested === "offer_letter" ? (
                  <p className="text-left mt-1 text-sm ">
                    {employment
                      ? `Start date: ${moment(employment[currentTypeOfSelfAttested]?.start_date).format("MMM YYYY")}`
                      : "-"}
                  </p>
                ) : (
                  <p className="text-left mt-1 text-sm ">
                    {employment
                      ? `${moment(employment[currentTypeOfSelfAttested]?.start_date).format("MMM DD, YYYY")} - ${endDate}`
                      : "-"}
                  </p>
                )}
              </div>
            );
          }}
        />
      </div>
    </Section>
  );
};
